import React, { useState, useEffect, useCallback, useMemo } from "react"
import { makeStyles, useTheme } from "@material-ui/styles"
import { Box, Button, Grid, MenuItem, Popover, useMediaQuery } from "@material-ui/core"
import { useHistory, useParams } from "react-router-dom"
import InfiniteScroll from "react-infinite-scroll-component"
import { useApolloClient } from "@apollo/client"
import { FilterList } from "@material-ui/icons"
import PopupState, { anchorRef, bindMenu, bindTrigger } from "material-ui-popup-state"
import {
  queryOrganisationTemplate,
  useLazyQueryOrganisationTemplates,
} from "../../data/jobs/useQueryOrganisationTemplates"
import {
  ProcessTemplateActionCard,
  LoadingSpinner,
  TaskTemplateActionCard,
  NoItemsMessage,
  TemplateCreator,
  JobCreator,
  SearchInput,
  ScrollToTop,
  IconMessage,
  OutlinedSelect,
  FiltersChips,
  ScheduleOutlinedSelect,
  LocationOutlinedSelect,
} from "../../components"
import { useProcessUtils } from "../../utils"
import { TemplateViewer } from "../../components/JobViewer/TemplateViewer"
import {
  useMutationUpdateProcessInactive,
  useMutationCopyProcess,
  useMutationDeleteProcess,
  useMutationUpdateTaskInactive,
  useQueryOrganisationTemplateCounts,
  useQueryGroups,
  useQueryLocations,
} from "../../data"

const Organisation = () => {
  const theme = useTheme()
  const client = useApolloClient()
  const isInline = useMediaQuery(theme.breakpoints.down("xs"))
  const { set, id, action, category } = useParams()
  const history = useHistory()

  const [data, setData] = useState([])
  const [total, setTotal] = useState(null)
  const [hasMore, setHasMore] = useState(false)
  const [dataLoading, setDataLoading] = useState(false)
  const [searchText, setSearchText] = useState(null)
  const [filters, setFilters] = useState([])
  const [editing, setEditing] = useState(null)
  const [viewing, setViewing] = useState(null)
  const [
    loadOrganisationTemplates,
    {
      data: organisationData,
      loading: organisationLoading,
      refetch: organisationRefetch,
      loadMore: organisationLoadMore,
    },
  ] = useLazyQueryOrganisationTemplates()
  const { refetch: countsRefetch } = useQueryOrganisationTemplateCounts()
  const [updateProcessInactive] = useMutationUpdateProcessInactive()
  const [updateTaskInactive] = useMutationUpdateTaskInactive()
  const [copyProcess] = useMutationCopyProcess()
  const [deleteProcess] = useMutationDeleteProcess()
  const { getNavigateToTemplateLink, getFriendlySetName } = useProcessUtils()

  const baseUri = `/templates/${set}`

  const hasSchedules = useMemo(() => set?.includes("recurring"), [set])

  useEffect(() => {
    setDataLoading(true)
    const locations = filters.filter((filter) => filter.type === "locations").map((filter) => filter.value)
    const groups = filters.filter((filter) => filter.type === "groups").map((filter) => filter.value)
    const schedules = filters.filter((filter) => filter.type === "schedules").map((filter) => filter.value)

    loadOrganisationTemplates({
      variables: {
        set: set || "active-recurring",
        locations: locations.length ? locations : null,
        groups: groups.length ? groups : null,
        schedules: schedules.length && hasSchedules ? schedules : null,
        searchText,
      },
    })
  }, [filters, hasSchedules, loadOrganisationTemplates, searchText, set])

  useEffect(() => {
    if (set && organisationData) {
      setData([...organisationData.organisationTemplates.items])
      setTotal(organisationData.organisationTemplates.total)
      setHasMore(organisationData.organisationTemplates.items.length < organisationData.organisationTemplates.total)
    } else {
      setHasMore(false)
      setTotal(null)
      setData([])
    }
    setDataLoading(false)
  }, [organisationData, set])

  const updateViewEditStates = useCallback(
    async (targetAction, subject, setSubject, diversion, setDiversion) => {
      const items = organisationData ? organisationData.organisationTemplates.items : []
      if (organisationData && items.length && !organisationLoading) {
        if (action === targetAction && id && (!subject || subject.id !== id)) {
          if (diversion) setDiversion(null)
          const item = items.find((template) => template.id === id)
          if (!item) {
            const updateLoadTemplate = async () => {
              const result = await queryOrganisationTemplate(id, client)
              if (result && result.data) setSubject(result.data.organisationTemplate)
            }
            updateLoadTemplate()
            countsRefetch()
          } else setSubject(item)
        } else if (!id && subject) setSubject(null)
      }
    },
    [action, client, countsRefetch, id, organisationData, organisationLoading]
  )

  useEffect(() => {
    updateViewEditStates("edit", editing, setEditing, viewing, setViewing)
  }, [editing, updateViewEditStates, viewing])

  useEffect(() => {
    updateViewEditStates("view", viewing, setViewing, editing, setEditing)
  }, [editing, updateViewEditStates, viewing])

  const handleCreateClose = async () => {
    await organisationRefetch()
    await countsRefetch()
    history.push(baseUri)
  }

  const handleEditProcess = (item) => {
    history.push(getNavigateToTemplateLink(item, set || "library", category, "edit"))
  }
  const handleCopyProcess = async ({ id: copyId }) => {
    const item = await copyProcess({ variables: { id: copyId } })
    await organisationRefetch()
    await countsRefetch()
    const inactiveSet = set.startsWith("active") ? `in${set}` : set
    if (item.data) history.push(`/templates/${inactiveSet}/${item.data.copyProcess.id}/edit`)
  }
  const handleToggleProcessInactive = async ({ id: processId, inactive }, navigateToTemplate) => {
    const item = await updateProcessInactive({ variables: { id: processId, inactive: !inactive } })
    await organisationRefetch()
    await countsRefetch()
    if (navigateToTemplate && item.data) {
      setViewing(item.data.updateProcessInactive)
      history.push(getNavigateToTemplateLink(item.data.updateProcessInactive, "", "", "view"))
    }
  }
  const handleDeleteProcess = async ({ id: processId }) => {
    await deleteProcess({ variables: { id: processId } })
    await organisationRefetch()
    await countsRefetch()
  }
  const handleUsed = async (item) => {
    await organisationRefetch()
    await countsRefetch()
    const inactiveSet = !set ? "inactive-oneoff" : set.startsWith("active") ? `in${set}` : set
    history.push(`/templates/${inactiveSet}/${item.id}/edit`)
  }
  const handleClose = async () => {
    history.push(baseUri)
  }
  const handleEditTask = (item) => {
    history.push(getNavigateToTemplateLink(item, set || "library", category, "edit"))
  }
  const handleTaskInactive = async ({ id: taskId }) => {
    await updateTaskInactive({ variables: { id: taskId, inactive: true } })
    await organisationRefetch()
    await countsRefetch()
  }

  const handleChangeSearchText = async (text) => {
    setSearchText(text || null)
  }

  const handleFilterChange = (type, values) => {
    setFilters((prev) => {
      const next = prev.filter((filter) => filter.type !== type)
      next.push(...values.map((value) => ({ type, value })))
      return next
    })
  }

  const handleClearFilters = () => {
    setFilters([])
  }

  const handleFilterDelete = ({ type, value }) => {
    setFilters((prev) => prev.filter((filter) => !(filter.type === type && filter.value === value)))
  }

  const heading = getFriendlySetName(set)

  const openNew = id === "new" && action

  const scrollToTop = isInline && (openNew || !!viewing || !!editing)

  const loading = organisationLoading || dataLoading

  const hasFilters = !!searchText || !!filters.length

  return (
    <>
      {scrollToTop && <ScrollToTop />}

      <JobCreator open={openNew} onClose={handleCreateClose} />

      <TemplateCreator open={editing !== null} onClose={handleClose} isInline={isInline} edit={editing} />

      <TemplateViewer
        template={viewing}
        onUsed={handleUsed}
        onDisable={handleToggleProcessInactive}
        onClose={handleClose}
      />

      {(!isInline || (isInline && !viewing && !editing && !openNew)) && (
        <>
          <SearchInput
            placeholder={`Search ${heading.toLowerCase()}`}
            debounce={300}
            onChange={handleChangeSearchText}
            loading={loading}
            boxProps={{ pl: 0, pr: 0, mb: isInline ? 2 : 3 }}
          />

          <FiltersPanel
            filters={filters}
            hasSchedules={hasSchedules}
            onChange={handleFilterChange}
            onClear={handleClearFilters}
            onDelete={handleFilterDelete}
          />

          {loading && (!data.length || !organisationData) && (
            <Box display="flex" justifyContent="center">
              <LoadingSpinner size={60} />
            </Box>
          )}
          <InfiniteScroll
            dataLength={data.length}
            next={organisationLoadMore}
            hasMore={hasMore}
            loader={
              <Box display="flex" justifyContent="center">
                <LoadingSpinner size={60} />
              </Box>
            }
            endMessage={
              !loading &&
              !!total && (
                <NoItemsMessage>
                  {data.length || "No"} matching item{data.length > 1 ? "s" : ""}
                </NoItemsMessage>
              )
            }
            style={{ overflow: "visible" }}
          >
            <Box data-cy="Box-templates">
              {data.map((template) => (
                <Box key={template.id} px={0}>
                  {template.__typename === "Process" && (
                    <ProcessTemplateActionCard
                      process={template}
                      set={set || "library"}
                      category={category}
                      onEdit={handleEditProcess}
                      onCopy={handleCopyProcess}
                      onDisable={handleToggleProcessInactive}
                      onDelete={handleDeleteProcess}
                    />
                  )}
                  {template.__typename === "Task" && (
                    <TaskTemplateActionCard
                      task={template}
                      set={set}
                      onEdit={handleEditTask}
                      onDisable={handleTaskInactive}
                    />
                  )}
                </Box>
              ))}
            </Box>
          </InfiniteScroll>
          {!loading && Boolean(organisationData) && !data.length && !hasFilters && (
            <IconMessage
              name="empty-templates"
              text={`You have no ${heading.toLowerCase()}.${
                !!set && !set.startsWith("inactive") ? " Why not add one?" : ""
              }`}
            />
          )}
          {!loading && Boolean(organisationData) && !data.length && hasFilters && (
            <NoItemsMessage>No items match your search</NoItemsMessage>
          )}
        </>
      )}
    </>
  )
}

const useFiltersPanelStyles = makeStyles((theme) => ({
  fullName: {
    fontSize: 16,
    color: theme.palette.text.tinted,
    lineHeight: "20px",
  },
  roles: {
    color: theme.palette.text.light,
  },
  filter: {
    backgroundColor: theme.palette.shaded.filter,
  },
  menu: {
    marginTop: 5,
  },
  clearAllLink: {
    whiteSpace: "nowrap",
  },
  infiniteScrollWrapper: {
    "&&>div": {
      display: "contents",
    },
  },
  infiniteScroll: {
    display: "contents",
  },
}))

const FiltersPanel = ({ filters, hasSchedules, onChange, onClear, onDelete }) => {
  const classes = useFiltersPanelStyles()
  const { data: groupsData } = useQueryGroups()
  const { data: locationsData } = useQueryLocations()

  const data = {
    groupsData,
    locationsData,
  }

  return (
    <Box display="flex" flexDirection="row">
      <Box px={3} mb={3} display="flex" flexGrow={1} className={classes.filter}>
        <Box py={3} pr={2}>
          <FiltersButton filters={filters} hasSchedules={hasSchedules} onFilterChange={onChange} />
        </Box>
        <Box display="flex" alignItems="center">
          <FiltersChips data={data} filters={filters} hasSchedules={hasSchedules} onDelete={onDelete} />
        </Box>
        {filters.length > 0 && (
          <Box ml="auto" pl={2} display="flex" alignItems="center">
            <Button className={classes.clearAllLink} color="primary" onClick={onClear}>
              Clear all
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  )
}

const useFiltersPopoverStyles = makeStyles((theme) => ({
  root: {
    width: 512,
    marginTop: theme.spacing(1),
  },
  paper: {
    width: "100%",
    maxWidth: "calc(100vw - 32px)",
  },
}))

const FiltersButton = ({ filters, onFilterChange, hasSchedules }) => {
  const filtersPopoverClasses = useFiltersPopoverStyles()
  const { data: groupsData } = useQueryGroups()

  const handleFilterChange = (type, eventOrValues) => {
    let values = eventOrValues.target?.value || eventOrValues
    if (values.includes("all")) {
      values = []
    }
    if (onFilterChange) onFilterChange(type, values)
  }

  return (
    <PopupState variant="popover" popupId="bulk-actions">
      {(popupState) => (
        <>
          <Button variant="contained" color="primary" {...bindTrigger(popupState)} ref={anchorRef(popupState)}>
            <FilterList />
            <Box ml={1}>Filters</Box>
          </Button>
          <Popover
            {...bindMenu(popupState)}
            getContentAnchorEl={null}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            classes={filtersPopoverClasses}
          >
            <Box p={2}>
              <Grid container direction="row" spacing={2}>
                <Grid item xs={6}>
                  <OutlinedSelect
                    label="Areas of work"
                    native={false}
                    value={filters.filter((filter) => filter.type === "groups").map((filter) => filter.value)}
                    onChange={(event) => handleFilterChange("groups", event)}
                    multiple
                  >
                    {groupsData &&
                      groupsData.groups.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                  </OutlinedSelect>
                </Grid>
                {hasSchedules && (
                  <Grid item xs={6}>
                    <ScheduleOutlinedSelect
                      label="Schedules"
                      value={filters.filter((filter) => filter.type === "schedules").map((filter) => filter.value)}
                      multiple
                      hasAll={false}
                      hasNonRecurring={false}
                      onChange={(event) => handleFilterChange("schedules", event)}
                    />
                  </Grid>
                )}
                <Grid item xs={hasSchedules ? 12 : 6}>
                  <LocationOutlinedSelect
                    label="Locations"
                    value={filters.filter((filter) => filter.type === "locations").map((filter) => filter.value)}
                    onChange={(event) => handleFilterChange("locations", event)}
                    onRegionChange={(event) => handleFilterChange("locations", event)}
                    multiple
                  />
                </Grid>
              </Grid>
            </Box>
          </Popover>
        </>
      )}
    </PopupState>
  )
}

export default Organisation
